/**
 * DataTable — implements the A3 data-table contract of /docs/design.md.
 *
 * Built for the three real cases of this product: the ETF catalogue (many rows,
 * selection, search), Total Holdings / Results (numeric columns, sticky
 * identifier, sort) and the future comparison tables. Nothing speculative:
 * every prop below has a caller.
 *
 * Column: {
 *   key, header, numeric, sticky, sortable (default true), accessor,
 *   width, minWidth, className, headerClassName, render(row), title
 * }
 */
import { useMemo } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Check } from "lucide-react";
import { useTableSort } from "@/hooks/useTableSort";
import { SkeletonTable } from "@/components/ui/skeleton";
import { EmptyState, ErrorState } from "@/components/system/states";

const DENSITY = {
  default: { cell: "px-3 py-2.5", head: "px-3 py-2.5" },
  compact: { cell: "px-2.5 py-1.5", head: "px-2.5 py-2" },
};

function SortButton({ column, sortField, sortDir, onSort }) {
  const active = sortField === column.key && sortDir !== 0;
  let Icon = ArrowUpDown;
  if (active) Icon = sortDir === 1 ? ArrowUp : ArrowDown;
  const next = !active ? "crescente" : sortDir === 1 ? "decrescente" : "ordine originale";
  return (
    <button
      type="button"
      data-testid={`datatable-sort-${column.key}`}
      onClick={() => onSort(column.key)}
      aria-label={`Ordina per ${typeof column.header === "string" ? column.header : column.key}: ${next}`}
      className={`inline-flex items-center gap-1 min-h-10 -my-1 rounded-md px-0.5 max-w-full min-w-0 text-label uppercase
                  text-content-secondary hover:text-content transition-colors duration-fast ease-system
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus
                  ${column.numeric ? "flex-row-reverse" : ""}`}
    >
      <span className="min-w-0 text-left break-words">{column.header}</span>
      <Icon
        className={`w-3.5 h-3.5 shrink-0 ${active ? "text-brand-accent" : "text-content-muted opacity-60"}`}
        aria-hidden="true"
      />
    </button>
  );
}

export function DataTable({
  columns,
  rows,
  getRowId = (row, i) => row?.id ?? i,
  density = "default",
  initialSort = null,
  accessors,
  loading = false,
  error = null,
  onRetry,
  empty,
  emptyTitle = "Nessun risultato",
  emptyDescription,
  emptyAction,
  selectable = false,
  isRowSelected,
  onToggleRow,
  onRowClick,
  rowTestId,
  rowClassName,
  totalCount,
  countLabel = "righe",
  limit,
  maxHeight = "60vh",
  caption,
  fitWidth = false,
  className = "",
  testId = "data-table",
  footer,
}) {
  const d = DENSITY[density] || DENSITY.default;
  const sortAccessors = useMemo(() => {
    const acc = { ...(accessors || {}) };
    columns.forEach((c) => {
      if (c.accessor && !acc[c.key]) acc[c.key] = c.accessor;
    });
    return acc;
  }, [accessors, columns]);

  const { sorted, sortField, sortDir, toggle } = useTableSort(rows || [], {
    accessors: sortAccessors,
    initial: initialSort,
  });

  // `limit` caps the rendered rows AFTER sorting, so the cap always shows the
  // top N of the order the user chose (the historical Total Holdings rule).
  const visible = limit ? sorted.slice(0, limit) : sorted;

  const isEmpty = empty ?? (!loading && !error && (rows || []).length === 0);
  const shown = visible.length;
  const total = totalCount ?? (rows || []).length;

  if (error) {
    return (
      <div className={`rounded-card border border-line-soft bg-surface ${className}`} data-testid={testId}>
        <ErrorState
          description={typeof error === "string" ? error : undefined}
          onRetry={onRetry}
          testId={`${testId}-error`}
        />
      </div>
    );
  }

  return (
    <div className={className} data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-label text-content-muted tabular" data-testid={`${testId}-count`}>
          {loading ? "Caricamento…" : `${shown} di ${total} ${countLabel}`}
        </p>
        {footer}
      </div>

      <div className="relative rounded-card border border-line-soft bg-surface overflow-hidden">
        {/* A3.8 — horizontal scrolling is explicit and always reachable */}
        <div
          className="overflow-auto scrollable-table-scroll-area"
          style={{ maxHeight }}
          tabIndex={0}
          role="region"
          aria-label={caption || "Tabella dati"}
        >
          {loading ? (
            <div className="p-4">
              <SkeletonTable rows={6} cols={Math.min(columns.length, 5)} />
            </div>
          ) : isEmpty ? (
            <EmptyState
              title={emptyTitle}
              description={emptyDescription}
              action={emptyAction}
              testId={`${testId}-empty`}
            />
          ) : (
            <table className={`${fitWidth ? "w-full table-fixed" : "w-max min-w-full"} border-collapse`}>
              {caption && <caption className="sr-only">{caption}</caption>}
              <thead>
                <tr>
                  {selectable && (
                    <th
                      scope="col"
                      style={fitWidth ? { width: 40 } : undefined}
                      className={`sticky top-0 left-0 z-30 bg-table-header border-b border-line ${d.head} w-10`}
                    >
                      <span className="sr-only">Selezione</span>
                    </th>
                  )}
                  {columns.map((c) => {
                    const sortable = c.sortable !== false;
                    let ariaSort = "none";
                    if (sortField === c.key && sortDir !== 0) {
                      ariaSort = sortDir === 1 ? "ascending" : "descending";
                    }
                    return (
                      <th
                        key={c.key}
                        scope="col"
                        aria-sort={sortable ? ariaSort : undefined}
                        style={{ width: c.width, minWidth: c.minWidth }}
                        className={`sticky top-0 z-20 bg-table-header border-b border-line
                                    ${fitWidth ? "overflow-hidden" : "whitespace-nowrap"}
                                    text-label uppercase text-content-secondary font-semibold ${d.head}
                                    ${c.numeric ? "text-right" : "text-left"}
                                    ${c.sticky ? "left-0 z-30" : ""} ${c.headerClassName || ""}`}
                      >
                        {sortable ? (
                          <SortButton column={c} sortField={sortField} sortDir={sortDir} onSort={toggle} />
                        ) : (
                          <span className="truncate">{c.header}</span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visible.map((row, i) => {
                  const id = getRowId(row, i);
                  const selected = selectable && isRowSelected ? isRowSelected(row) : false;
                  const clickable = Boolean(onRowClick || (selectable && onToggleRow));
                  return (
                    <tr
                      key={id}
                      data-testid={rowTestId ? `${rowTestId}-${id}` : undefined}
                      data-selected={selected ? "true" : undefined}
                      aria-selected={selectable ? selected : undefined}
                      onClick={clickable
                        ? () => (onRowClick ? onRowClick(row) : onToggleRow(row))
                        : undefined}
                      className={`border-b border-line-soft
                                  ${selected ? "bg-table-selected" : "hover:bg-table-hover"}
                                  ${clickable ? "cursor-pointer" : ""}
                                  ${rowClassName ? rowClassName(row) : ""}`}
                    >
                      {selectable && (
                        <td className={`sticky left-0 z-10 ${selected ? "bg-table-selected" : "bg-surface"} ${d.cell}`}>
                          {/* A5 (v2.1) — the visual cue may be 16px, the target
                              may not: it lives inside a 40×40 control that is
                              reachable and operable from the keyboard. */}
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={selected}
                            aria-label={selected ? "Deseleziona riga" : "Seleziona riga"}
                            data-testid={rowTestId ? `${rowTestId}-${id}-select` : undefined}
                            onClick={(e) => { e.stopPropagation(); onToggleRow?.(row); }}
                            onKeyDown={(e) => {
                              if (e.key === " " || e.key === "Enter") {
                                e.preventDefault();
                                e.stopPropagation();
                                onToggleRow?.(row);
                              }
                            }}
                            className="flex items-center justify-center w-10 h-10 -my-2 -mx-2 rounded-md
                                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                          >
                            <span
                              aria-hidden="true"
                              className={`flex items-center justify-center w-4 h-4 rounded border
                                          ${selected ? "bg-brand border-brand" : "border-line-strong"}`}
                            >
                              {selected && <Check className="w-3.5 h-3.5 text-content-on-brand" />}
                            </span>
                          </button>
                        </td>
                      )}
                      {columns.map((c) => (
                        <td
                          key={c.key}
                          title={c.title ? c.title(row) : undefined}
                          className={`${d.cell} text-data text-content-secondary
                                      ${c.numeric ? "text-right tabular" : "text-left"}
                                      ${c.sticky ? `sticky left-0 z-10 ${selected ? "bg-table-selected" : "bg-surface"}` : ""}
                                      ${c.className || ""}`}
                        >
                          {c.render ? c.render(row) : row[c.key]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default DataTable;
